/**
 * ClipboardParser - HTML and RTF parsing for rich clipboard paste
 *
 * Phase 2 Implementation: Parse clipboard content from external applications
 * (Word, Google Docs, web pages) and convert to document format.
 *
 * Supports:
 * - HTML parsing (from web, Word, Google Docs)
 * - RTF parsing (legacy Office apps)
 * - Paragraph break detection
 * - Basic text styling (bold, italic, font family, font size, color)
 */

// ============================================================================
// Types
// ============================================================================

export interface ParsedRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  fontFamily?: string;
  fontSize?: number;
  color?: string;
}

export interface ParsedParagraph {
  runs: ParsedRun[];
}

export interface ParsedClipboardContent {
  paragraphs: ParsedParagraph[];
  plainText: string;
}

// ============================================================================
// HTML Parser
// ============================================================================

/**
 * Parse HTML content from clipboard into structured paragraphs and runs.
 *
 * Handles HTML from:
 * - Microsoft Word (uses <p> tags with mso-* styles)
 * - Google Docs (uses <p> tags with inline styles)
 * - Web pages (various HTML structures)
 *
 * @param html - Raw HTML string from clipboard
 * @returns Parsed content with paragraphs, runs, and plain text
 */
export function parseHTMLToContent(html: string): ParsedClipboardContent {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const paragraphs: ParsedParagraph[] = [];
  let plainText = '';

  // Walk the DOM tree and extract text with formatting
  function walkNode(node: Node, inheritedStyle: Partial<ParsedRun>): ParsedRun[] {
    const runs: ParsedRun[] = [];

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text) {
        runs.push({
          text,
          ...inheritedStyle
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tagName = el.tagName.toUpperCase();
      const style = { ...inheritedStyle };

      // Detect formatting from HTML tags
      if (tagName === 'B' || tagName === 'STRONG') {
        style.bold = true;
      }
      if (tagName === 'I' || tagName === 'EM') {
        style.italic = true;
      }
      if (tagName === 'U') {
        style.underline = true;
      }

      // Detect formatting from inline styles
      const computedStyle = el.style;
      if (computedStyle.fontWeight === 'bold' || parseInt(computedStyle.fontWeight) >= 700) {
        style.bold = true;
      }
      if (computedStyle.fontStyle === 'italic') {
        style.italic = true;
      }
      if (computedStyle.textDecoration?.includes('underline')) {
        style.underline = true;
      }
      if (computedStyle.fontFamily) {
        // Clean up font family (remove quotes, fallbacks)
        style.fontFamily = computedStyle.fontFamily.split(',')[0].replace(/["']/g, '').trim();
      }
      if (computedStyle.fontSize) {
        const sizeMatch = computedStyle.fontSize.match(/(\d+(?:\.\d+)?)/);
        if (sizeMatch) {
          style.fontSize = parseFloat(sizeMatch[1]);
        }
      }
      if (computedStyle.color && computedStyle.color !== 'inherit') {
        style.color = computedStyle.color;
      }

      // Handle block-level elements as paragraph boundaries
      const isBlockElement = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'PRE'].includes(tagName);
      const isLineBreak = tagName === 'BR';

      if (isBlockElement) {
        // Start collecting runs for this paragraph
        const paragraphRuns: ParsedRun[] = [];

        for (const child of el.childNodes) {
          paragraphRuns.push(...walkNode(child, style));
        }

        // Only add non-empty paragraphs
        if (paragraphRuns.length > 0 && paragraphRuns.some(r => r.text.trim())) {
          paragraphs.push({ runs: paragraphRuns });
          plainText += paragraphRuns.map(r => r.text).join('') + '\r';
        }
      } else if (isLineBreak) {
        // BR tag = soft line break within paragraph
        runs.push({ text: '\n', ...style });
      } else {
        // Inline element - continue walking
        for (const child of el.childNodes) {
          runs.push(...walkNode(child, style));
        }
      }
    }

    return runs;
  }

  // Start walking from body
  const body = doc.body;

  // Check if there are block-level elements
  const hasBlockElements = body.querySelector('p, div, h1, h2, h3, h4, h5, h6, li, blockquote, pre');

  if (hasBlockElements) {
    // Walk and let block elements create paragraphs
    walkNode(body, {});
  } else {
    // No block elements - treat entire content as one paragraph
    const runs = walkNode(body, {});
    if (runs.length > 0) {
      paragraphs.push({ runs });
      plainText = runs.map(r => r.text).join('');
    }
  }

  // If no paragraphs were created, extract plain text
  if (paragraphs.length === 0) {
    const textContent = body.textContent || '';
    if (textContent) {
      paragraphs.push({ runs: [{ text: textContent }] });
      plainText = textContent;
    }
  }

  // Clean up trailing newline from plainText
  if (plainText.endsWith('\r')) {
    plainText = plainText.slice(0, -1);
  }

  return {
    paragraphs,
    plainText
  };
}

/**
 * Convert parsed content to plain text with paragraph breaks.
 * Uses \r for paragraph breaks (MS Word model).
 *
 * @param content - Parsed clipboard content
 * @returns Plain text string with \r paragraph breaks
 */
export function parsedContentToPlainText(content: ParsedClipboardContent): string {
  return content.paragraphs
    .map(para => para.runs.map(run => run.text).join(''))
    .join('\r');
}

// ============================================================================
// RTF Parser (Basic)
// ============================================================================

/**
 * Parse RTF content from clipboard.
 * This is a basic implementation that extracts plain text.
 * Full RTF parsing is complex - this handles common cases.
 *
 * @param rtf - Raw RTF string from clipboard
 * @returns Parsed content (currently just extracts plain text)
 */
export function parseRTFToContent(rtf: string): ParsedClipboardContent {
  // Basic RTF text extraction
  // Remove RTF control words and extract plain text
  let text = rtf;

  // Remove RTF header and groups
  text = text.replace(/\{\\rtf[^}]*\}/g, '');
  text = text.replace(/\\[a-z]+(-?\d+)?\s?/gi, '');
  text = text.replace(/[{}]/g, '');

  // Convert RTF line breaks to paragraph breaks
  text = text.replace(/\\par\s*/g, '\r');
  text = text.replace(/\\line\s*/g, '\n');

  // Unescape special characters
  text = text.replace(/\\\\/g, '\\');
  text = text.replace(/\\'/g, "'");

  // Clean up
  text = text.trim();

  const paragraphs: ParsedParagraph[] = text.split('\r').map(paraText => ({
    runs: [{ text: paraText }]
  }));

  return {
    paragraphs,
    plainText: text.replace(/\r/g, '\r')
  };
}

// ============================================================================
// Smart Paste Detection
// ============================================================================

/**
 * Determine the best format to use from clipboard data.
 *
 * Priority:
 * 1. EdgeLex native format (full fidelity)
 * 2. HTML (from Word, Google Docs, web)
 * 3. RTF (legacy Office)
 * 4. Plain text (fallback)
 *
 * @param clipboardData - ClipboardEvent.clipboardData
 * @returns Best format to use and the data
 */
export function detectClipboardFormat(clipboardData: DataTransfer): {
  format: 'edgelex' | 'html' | 'rtf' | 'plain';
  data: string;
} {
  // Check for EdgeLex native format first
  const edgelexData = clipboardData.getData('application/x-edgelex-document');
  if (edgelexData) {
    return { format: 'edgelex', data: edgelexData };
  }

  // Check for HTML (most common from Word, Google Docs, web)
  const html = clipboardData.getData('text/html');
  if (html && html.trim()) {
    return { format: 'html', data: html };
  }

  // Check for RTF
  const rtf = clipboardData.getData('text/rtf');
  if (rtf && rtf.trim()) {
    return { format: 'rtf', data: rtf };
  }

  // Fallback to plain text
  const text = clipboardData.getData('text/plain');
  return { format: 'plain', data: text || '' };
}

// ============================================================================
// Main Entry Point
// ============================================================================

/**
 * Parse clipboard data and return structured content.
 * Automatically detects the best format and parses accordingly.
 *
 * @param clipboardData - ClipboardEvent.clipboardData
 * @returns Parsed content with paragraphs and plain text
 */
export function parseClipboardData(clipboardData: DataTransfer): ParsedClipboardContent {
  const { format, data } = detectClipboardFormat(clipboardData);

  switch (format) {
    case 'edgelex':
      // EdgeLex native format - parse JSON
      try {
        const parsed = JSON.parse(data);
        return {
          paragraphs: parsed.content?.paragraphs || [],
          plainText: parsed.content?.text || ''
        };
      } catch {
        // Fallback to plain text if JSON parse fails
        return { paragraphs: [], plainText: data };
      }

    case 'html':
      return parseHTMLToContent(data);

    case 'rtf':
      return parseRTFToContent(data);

    case 'plain':
    default:
      // Convert plain text to paragraphs
      const paragraphs = data.split(/\r\n|\r|\n/).map(line => ({
        runs: [{ text: line }]
      }));
      return {
        paragraphs,
        plainText: data
      };
  }
}
